import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";

export default function CombineScores({ userId }) {

  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState();
  const [skillScores, setSkillScores] = useState([]);
  const [combineScores, setCombineScores] = useState([]);
  const [userSkillScores, setUserSkillScores] = useState({});
  const [userCombineScores, setUserCombineScores] = useState({});

  // Fetch programs
  useEffect(() => {
    async function fetchPrograms() {
      const { data, error } = await supabase
        .from("program_coaches")
        .select("program_id, programs(id,name)")
        .eq("user_id", userId);
      if (error) console.error(error);
      else setPrograms(data || []);
    }
    fetchPrograms();
  }, []);

  // Fetch metrics and user scores
  useEffect(() => {
    if (!selectedProgram) return;

    async function fetchScores() {
      // Skill metrics
      const { data: skillMetrics, error: skillError } = await supabase
        .from("program_goal_skill_scores")
        .select("id, skill_name, target_score")
        .eq("program_id", selectedProgram);
      if (skillError) console.error(skillError);
      else setSkillScores(skillMetrics || []);

      // Combine metrics
      const { data: combineMetrics, error: combineError } = await supabase
        .from("program_goal_combine_scores")
        .select("id, drill_name, target_score")
        .eq("program_id", selectedProgram);
      if (combineError) console.error(combineError);
      else setCombineScores(combineMetrics || []);

      // User skill scores
      if ((skillMetrics || []).length > 0) {
        const { data: userSkills } = await supabase
          .from("user_skill_scores")
          .select("program_skill_score, user_score")
          .eq("athlete_id", userId)
          .eq("program_id", selectedProgram)
          .in(
            "program_skill_score",
            skillMetrics.map((s) => s.id)
          );
        const skillMap = {};
        (userSkills || []).forEach((s) => {
          skillMap[s.program_skill_score] = s.user_score;
        });
        setUserSkillScores(skillMap);
      }

      // User combine scores
      if ((combineMetrics || []).length > 0) {
        const { data: userCombines } = await supabase
          .from("user_combine_score")
          .select("program_combine_scores, user_score")
          .eq("athlete_id", userId)
          .eq("program_id", selectedProgram)
          .in(
            "program_combine_scores",
            combineMetrics.map((c) => c.id)
          );
        const combineMap = {};
        (userCombines || []).forEach((c) => {
          combineMap[c.program_combine_scores] = c.user_score;
        });
        setUserCombineScores(combineMap);
      }
    }

    fetchScores();
  }, [selectedProgram]);

  // Helper to determine strength or weakness
  const getCardClass = (userScore, targetScore) => {
    if (!userScore) return "bg-white";
    return Number(userScore) >= Number(targetScore)
      ? "bg-green-100" // strength
      : "bg-red-100"; // weakness
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Goal Scores</h1>

      <div className="mb-4">
        <label className="block mb-2 font-semibold">Select Program:</label>
        <select
          className="border p-2 rounded w-full max-w-xs"
          value={selectedProgram || ""}
          onChange={(e) => setSelectedProgram(e.target.value)}
        >
          <option value="" disabled>
            -- Choose Program --
          </option>
          {programs.length > 0 ? (
            programs.map((p) => (
              <option key={p.program_id} value={p.program_id}>
                {p.programs?.name || "Unnamed Program"}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No programs available
            </option>
          )}
        </select>
      </div>

      {skillScores.length > 0 && (
        <>
          <h2 className="text-xl font-semibold mb-2">Skill Scores</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {skillScores.map((metric) => (
              <div
                key={metric.id}
                className={`${getCardClass(
                  userSkillScores[metric.id],
                  metric.target_score
                )} p-4 rounded shadow hover:shadow-lg transition`}
              >
                <h3 className="font-semibold">{metric.skill_name}</h3>
                <p className="text-gray-500 text-sm mb-2">
                  Target: {metric.target_score}
                </p>
              </div>
            ))}
          </div>
        </>
      )}

      {combineScores.length > 0 && (
        <>
          <h2 className="text-xl font-semibold mb-2">Combine Scores</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {combineScores.map((metric) => (
              <div
                key={metric.id}
                className={`${getCardClass(
                  userCombineScores[metric.id],
                  metric.target_score
                )} p-4 rounded shadow hover:shadow-lg transition`}
              >
                <h3 className="font-semibold">{metric.drill_name}</h3>
                <p className="text-gray-500 text-sm mb-2">
                  Target: {metric.target_score}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}